'use client';

import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { useEffect, type ReactNode } from 'react';
import { useInView, type SpringValue } from 'react-spring';
import { GRUND } from './rörelse-konfig';
import { useScenframsteg } from './useScenframsteg';

export type ScenKontext = {
    /** Sant medan scenen är i vyporten. Armerar scroll-arbetet och släpper det igen. */
    synlig: boolean;
    /** 0 → 1 medan scenen passerar. Interpolera med .to(), lyft aldrig in i state. */
    framsteg: SpringValue<number>;
};

type Props = {
    /** Ankare och navigeringsmål. */
    id: string;
    höjd?: 'skärm' | 'auto';
    /** Anropas när scenen kommer in i eller lämnar vyn. Låter sidan flytta sin ljuskälla. */
    onSynlig?: (synlig: boolean) => void;
    sx?: SxProps<Theme>;
    children: ReactNode | ((kontext: ScenKontext) => ReactNode);
};

/**
 * En scen. Armerar sitt scroll-arbete strax innan den syns och släpper det när den
 * lämnat vyporten, så en stillastående sida kostar noll.
 *
 * `content-visibility: auto` är den enskilt största vinsten på en sida med sju scener:
 * webbläsaren hoppar över renderingsarbetet för allt som ligger utanför vyporten.
 * `contain-intrinsic-size` håller rullningslisten stabil medan den gör det.
 */
export function Scen({ id, höjd = 'skärm', onSynlig, sx, children }: Props) {
    const [ref, synlig] = useInView({ amount: 0.35, rootMargin: '15% 0px' });
    const framsteg = useScenframsteg(ref, synlig);

    useEffect(() => {
        onSynlig?.(synlig);
    }, [synlig, onSynlig]);

    return (
        <Box
            component="section"
            id={id}
            ref={ref}
            sx={{
                position: 'relative',
                minHeight: höjd === 'skärm' ? '100dvh' : undefined,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                px: { xs: 3, sm: 6, md: 10 },
                py: { xs: 10, md: 14 },
                contentVisibility: 'auto',
                containIntrinsicSize: 'auto 100dvh',
                color: GRUND.text,
                ...sx
            }}
        >
            {typeof children === 'function' ? children({ synlig, framsteg }) : children}
        </Box>
    );
}
